import type { Permission, Role, RoleDefinition } from '@/types/domain';

/**
 * Role & permission registry.
 *
 * IMPORTANT: The UI must never rely on client-side hiding alone for security.
 * This registry defines the *intended* access model so a backend can enforce
 * exactly the same rules (row-level security / role-based authorization) once
 * connected. `hasPermission` here is for UX only.
 */

const BASE: Permission[] = ['catalog.view', 'pricing.base', 'quotes.create'];

export const ROLE_DEFINITIONS: Record<Role, RoleDefinition> = {
  member: {
    id: 'member',
    labelKey: 'roles.member',
    group: 'customer',
    tier: 'standard',
    partnerType: null,
    permissions: [...BASE, 'orders.create', 'orders.own', 'payments.own'],
  },
  dealer_a: {
    id: 'dealer_a',
    labelKey: 'roles.dealer_a',
    group: 'dealer',
    tier: 'a',
    partnerType: null,
    permissions: [...BASE, 'pricing.wholesale', 'orders.create', 'orders.own', 'customers.manage', 'payments.own', 'logistics.track'],
  },
  dealer_b: {
    id: 'dealer_b',
    labelKey: 'roles.dealer_b',
    group: 'dealer',
    tier: 'b',
    partnerType: null,
    permissions: [...BASE, 'pricing.wholesale', 'orders.create', 'orders.own', 'payments.own', 'logistics.track'],
  },
  dealer_c: {
    id: 'dealer_c',
    labelKey: 'roles.dealer_c',
    group: 'dealer',
    tier: 'c',
    partnerType: null,
    permissions: [...BASE, 'pricing.wholesale', 'orders.create', 'orders.own', 'payments.own'],
  },
  distributor: {
    id: 'distributor',
    labelKey: 'roles.distributor',
    group: 'partner',
    tier: null,
    partnerType: 'distributor',
    permissions: [...BASE, 'pricing.wholesale', 'orders.create', 'orders.own'],
  },
  architect: {
    id: 'architect',
    labelKey: 'roles.architect',
    group: 'partner',
    tier: null,
    partnerType: 'architect',
    permissions: [...BASE, 'quotes.create'],
  },
  construction: {
    id: 'construction',
    labelKey: 'roles.construction',
    group: 'partner',
    tier: null,
    partnerType: 'construction',
    permissions: [...BASE, 'quotes.create'],
  },
  realtor: {
    id: 'realtor',
    labelKey: 'roles.realtor',
    group: 'partner',
    tier: null,
    partnerType: 'realtor',
    permissions: [...BASE, 'quotes.create'],
  },
  admin: {
    id: 'admin',
    labelKey: 'roles.admin',
    group: 'admin',
    tier: null,
    partnerType: null,
    permissions: ['admin.manage', 'orders.all', 'customers.manage', 'pricing.wholesale', 'logistics.track'],
  },
};

export function hasPermission(role: Role, permission: Permission): boolean {
  return ROLE_DEFINITIONS[role]?.permissions.includes(permission) ?? false;
}